import { ChangeDetectorRef, Component, Inject, PLATFORM_ID, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BlogType } from '@sifca-monorepo/ecommerce-generator';
import { identity, map, pickBy, range } from 'lodash';
import { catchError, filter, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { AppService, SeoService, SharedService } from '../../services';

declare const FB: any;

@Component({
  template: '',
})
export class BaseBlogDetailsComponent {
  private unsubscribeAll: Subject<any> = new Subject<any>();

  blog: BlogType;
  blogs: BlogType[];
  numberOfPages = 3;
  startingPages = 0;
  pageRange: number[];
  currentPage = 1;
  totalOfPages: number;
  sideBlogs: BlogType[];
  blogLimit = 2;
  isLoading = true;
  posId: string = '';
  seo: any;
  constructor(
    protected router: Router,
    protected renderer: Renderer2,
    protected appService: AppService,
    protected sharedService: SharedService,
    protected activatedRoute: ActivatedRoute,
    protected seoService: SeoService,
    protected titleTagService: Title,
    protected metaTagService: Meta,
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) protected document: Document,
    @Inject(PLATFORM_ID) protected platformId: Object,
  ) {
    this.sharedService.posId$.subscribe((posId) => {
      this.posId = posId;
    });
    this.appService.productsPageIndex$ = 0;
    this.appService.findBlogsByTargetPaginated(this.posId, 2).subscribe();
    this.activatedRoute.params
      .pipe(
        switchMap((params) => this.appService.findBlogsByTargetAndUrl(this.posId, params['url'])),
        switchMap(() => this.appService.blog$),
        filter((blog) => !!blog),
      )
      .subscribe((res) => {
        this.blog = res;
        this.getBlogSeo();
        this.isLoading = false;
        this.changeDetectorRef.markForCheck();
      });
    this.appService.blogs$.pipe(takeUntil(this.unsubscribeAll)).subscribe((blogs) => {
      if (blogs) {
        this.blogs = blogs.objects;
        this.totalOfPages = blogs.count / this.blogLimit;
        if (this.numberOfPages < this.totalOfPages) {
          this.pageRange = range(this.startingPages, this.numberOfPages);
        } else {
          this.pageRange = range(this.startingPages, this.totalOfPages);
        }
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  getBlogSeo() {
    if (this.blog) {
      this.seoService
        .getBlogSeoJSON(this.blog.id)
        .pipe(
          take(1),
          catchError(() => {
            return of(null);
          }),
        )
        .subscribe((res) => {
          this.seo = res;
          if (this.seo?.title) {
            this.titleTagService.setTitle(this.seo?.title);
          }
          this.sharedService.tags$ = [...this.metaTagService.addTags(map(this.seo?.metas, (meta) => pickBy(meta, identity))), { charset: 'UTF-8' }];
          this.sharedService.links$ = map(this.seo?.links, (seoLink) => {
            const link = this.renderer.createElement('link');
            if (seoLink.sizes) {
              link.sizes = seoLink.sizes;
            }
            if (seoLink.href) {
              link.href = seoLink.href;
            }
            if (seoLink.rel) {
              link.rel = seoLink.rel;
            }
            this.renderer.appendChild(this.document.head, link);
            return link;
          });
        });
    }
  }

  onRoute(param: string, blog: BlogType) {
    this.appService
      .findBlogsByTargetAndUrl(this.posId, blog.url)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
    this.router.navigateByUrl(param + blog.url);
  }

  getPage(page: number) {
    window.scrollTo(0, 0);
    this.appService.productsPageIndex$ = page;
    this.currentPage = page + 1;
    if (this.currentPage === this.pageRange.length && this.totalOfPages > this.numberOfPages + 3) {
      this.numberOfPages += 3;
      this.startingPages += 3;
      this.pageRange = range(this.startingPages, this.numberOfPages);
    } else {
      this.pageRange = range(this.startingPages, this.totalOfPages);
    }
    this.appService
      .findBlogsByTargetPaginated(this.posId)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
  }

  getLastPage() {
    this.document.defaultView?.scrollTo(0, 0);
    this.appService.productsPageIndex$ = this.totalOfPages - 1;
    this.pageRange = range(this.totalOfPages - 1, this.totalOfPages);
    this.currentPage = this.totalOfPages;
    this.appService
      .findBlogsByTargetPaginated(this.posId)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
  }

  getFirstPage() {
    this.document.defaultView?.scrollTo(0, 0);
    this.currentPage = 1;
    this.appService.productsPageIndex$ = 0;
    this.pageRange = range(0, 2);
    this.appService
      .findBlogsByTargetPaginated(this.posId)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
  }

  loadPrevPage() {
   this.document.defaultView?.scrollTo(0, 0);
    this.currentPage -= 1;
    this.appService.productsPageIndex$ -= 1;
    this.pageRange = range(0, 2);
    this.appService
      .findBlogsByTargetPaginated(this.posId)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
  }

  loadNextPage() {
    this.document.defaultView?.scrollTo(0, 0);
    this.numberOfPages++;
    this.currentPage += 1;
    this.appService.productsPageIndex$ += 1;
    this.pageRange = range(0, 2);
    if (this.currentPage > this.pageRange.length) {
      this.pageRange = range(0, this.numberOfPages);
    }
    this.appService
      .findBlogsByTargetPaginated(this.posId)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
  }

  shareOnFacebook() {
    if (isPlatformBrowser(this.platformId)) {
      FB.ui(
        {
          method: 'share',
          href: `https://cocospacio.com/blog/${this.blog?.url}`,
          // href: `${window.location.href}/p/${this.product?.seo?.urlKey}`,
        },
        (response) => {
          if (response && !response.error_code) {
            // The user successfully shared the product on Facebook
            console.log('Shared successfully');
            // You can trigger any additional actions here
          } else {
            // There was an error or the user canceled the share
            console.error('Share error');
          }
        },
      );
    }
  }

  ngOnDestroy() {
    this.appService.productsPageIndex$ = 0;
    this.appService.searchString = '';
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
  }
}
